'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { Button, cn, Sheet, SheetClose, SheetContent, SheetFooter, SheetTitle, SheetTrigger } from '@nwwp/ui-shadcn'
import { useTrackingUrl } from '~/hooks/use-tracking-url'
import { CTA, SCHEDULE_TRACKING_OPTIONS } from '~/utils/ctas'
import { Logo } from '../Logo'

/** Burger SVG icon */
const BurgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

/** Close (X) SVG icon */
const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d={open ? 'M7 14L12 9L17 14' : 'M7 10L12 15L17 10'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

type DesktopMenu = 'about' | 'resources'

const DESKTOP_MENU_ITEMS: Record<DesktopMenu, Array<{ href: string; label: string }>> = {
  about: [
    { href: '/meet-your-partners', label: 'Meet Your Partners' },
    { href: '/nyc', label: 'Financial Advisor for NYC' },
    { href: '/equity', label: 'Financial Advisor for Tech' },
    { href: '/fees', label: 'Our Fees' },
    { href: '/faq', label: 'FAQ' },
    // { href: '/contact-us', label: 'Press' }, // removed pending redesign
  ],
  resources: [
    { href: '/tools', label: 'Free Guides & Tools' },
    { href: '/knowus', label: 'Watch & Listen' },
    { href: '/client-tools', label: 'Client Resources' },
  ],
}

type NavbarProps = {
  hideItems?: boolean
}
export const Navbar = ({ hideItems = false }: NavbarProps) => {
  const scheduleUrl = useTrackingUrl(CTA.SCHEDULE_FORM_PAGE, SCHEDULE_TRACKING_OPTIONS)

  const [isOpen, setIsOpen] = useState(false)
  const [activeDesktopMenu, setActiveDesktopMenu] = useState<DesktopMenu | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname() ?? '/'
  const [prevPathname, setPrevPathname] = useState(pathname)

  // Close menus on route change using React's derived-state pattern (setState during render).
  // This avoids a useEffect→setState cycle: React discards the render and immediately
  // re-renders with the updated state, so there is no extra paint.
  if (prevPathname !== pathname) {
    setPrevPathname(pathname)
    setIsOpen(false)
    setActiveDesktopMenu(null)
  }

  // Handle clicks outside the navbar (desktop submenu only — Sheet handles its own overlay)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveDesktopMenu(null)
      }
    }

    if (activeDesktopMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeDesktopMenu])

  const toggleDesktopMenu = (menu: DesktopMenu) => {
    setActiveDesktopMenu((currentMenu) => (currentMenu === menu ? null : menu))
  }

  return (
    <header role="banner" ref={containerRef} className="relative bg-card">
      {/* Top bar */}
      <div className="relative z-50 flex h-18 items-center justify-end bg-card px-4 shadow-navbar tablet:px-25">
        {/* Logo — centered on mobile, left-aligned on desktop */}
        <Link href="/">
          <div
            className={cn(
              'absolute inset-y-0 flex cursor-pointer items-center justify-center transition-[left] duration-200 ease-in-out',
              'left-1/2 -translate-x-1/2',
              'tablet:left-25 tablet:translate-x-0'
            )}
          >
            <Logo alignItems="flex-start" white={false} />
          </div>
        </Link>

        {!hideItems && (
          <>
            {/* Desktop nav links */}
            <nav className="hidden flex-row items-center gap-16 tablet:flex" aria-label="Primary">
              <DesktopMenuButton
                label="ABOUT US"
                menu="about"
                activeMenu={activeDesktopMenu}
                onClick={toggleDesktopMenu}
              />
              <DesktopMenuButton
                label="RESOURCES"
                menu="resources"
                activeMenu={activeDesktopMenu}
                onClick={toggleDesktopMenu}
              />
              <Button asChild variant="funnel" size="default" className="h-10 px-6 tracking-wide">
                <Link href={scheduleUrl}>SCHEDULE A CALL</Link>
              </Button>
            </nav>

            {/* Mobile Sheet trigger */}
            <div className="tablet:hidden">
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <button className="p-1 text-foreground" aria-label="Open menu">
                    <BurgerIcon />
                  </button>
                </SheetTrigger>

                <SheetContent
                  side="right"
                  showCloseButton={false}
                  className="flex w-full flex-col gap-0 bg-card p-0 tablet:max-w-full"
                >
                  {/* Visually hidden title for screen readers */}
                  <SheetTitle className="sr-only">Navigation menu</SheetTitle>

                  {/* Sheet header — logo centered, close right */}
                  <div className="flex h-18 shrink-0 items-center justify-between border-b border-border px-6">
                    <Link href="/" onClick={() => setIsOpen(false)}>
                      <Logo alignItems="flex-start" white={false} />
                    </Link>
                    <SheetClose asChild>
                      <button className="p-1 text-foreground" aria-label="Close menu">
                        <CloseIcon />
                      </button>
                    </SheetClose>
                  </div>

                  {/* Scrollable nav sections */}
                  <nav
                    aria-label="Mobile navigation"
                    className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6"
                  >
                    {/* About Us section */}
                    <div className="flex flex-col gap-4">
                      <span className="text-body-xs font-medium tracking-widest text-neutral-darker uppercase">
                        ABOUT US
                      </span>
                      <div className="flex flex-col gap-6 px-2">
                        {DESKTOP_MENU_ITEMS.about.map((item) => (
                          <Link
                            key={item.label}
                            href={item.href}
                            onClick={() => setIsOpen(false)}
                            className="text-body-bold text-foreground underline underline-offset-2 hover:text-neutral-darker"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-border" />

                    {/* Resources section */}
                    <div className="flex flex-col gap-4">
                      <span className="text-body-xs font-medium tracking-widest text-neutral-darker uppercase">
                        RESOURCES
                      </span>
                      <div className="flex flex-col gap-6 px-2">
                        {DESKTOP_MENU_ITEMS.resources.map((item) => (
                          <Link
                            key={item.label}
                            href={item.href}
                            onClick={() => setIsOpen(false)}
                            className="text-body-bold text-foreground underline underline-offset-2 hover:text-neutral-darker"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </nav>

                  {/* Sticky CTA */}
                  <SheetFooter className="shrink-0 border-t border-border px-6 py-4">
                    <Button asChild variant="funnel" className="h-14 w-full tracking-wide">
                      <Link href={scheduleUrl}>SCHEDULE A CALL</Link>
                    </Button>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          </>
        )}
      </div>

      {activeDesktopMenu && (
        <nav
          id="desktop-navbar-submenu"
          className="hidden h-18 items-center justify-center bg-card px-4 shadow-navbar tablet:flex"
          aria-label="Submenu"
        >
          <div className="flex w-full max-w-5xl items-center justify-center gap-4">
            {DESKTOP_MENU_ITEMS[activeDesktopMenu].map((item, index) => (
              <span key={item.label} className="flex items-center gap-4">
                <Link
                  href={item.href}
                  className="text-body-bold tracking-wide whitespace-nowrap text-foreground underline underline-offset-2 hover:text-neutral-darker"
                >
                  {item.label}
                </Link>
                {index < DESKTOP_MENU_ITEMS[activeDesktopMenu].length - 1 && (
                  <span aria-hidden="true" className="text-body-xs text-neutral-light">
                    •
                  </span>
                )}
              </span>
            ))}
          </div>
        </nav>
      )}
    </header>
  )
}

type DesktopMenuButtonProps = {
  activeMenu: DesktopMenu | null
  label: string
  menu: DesktopMenu
  onClick: (menu: DesktopMenu) => void
}

const DesktopMenuButton = ({ activeMenu, label, menu, onClick }: DesktopMenuButtonProps) => {
  const isOpen = activeMenu === menu

  return (
    <button
      type="button"
      aria-expanded={isOpen}
      aria-controls="desktop-navbar-submenu"
      onClick={() => onClick(menu)}
      className={cn(
        'flex items-center gap-2 text-body-bold tracking-wide whitespace-nowrap transition-colors hover:text-neutral-darker',
        isOpen ? 'text-neutral-darker' : 'text-foreground'
      )}
    >
      {label}
      <ChevronIcon open={isOpen} />
    </button>
  )
}
